import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** One period's aggregates for a line. `period_index` is chronological —
 * 0 = oldest, n_periods - 1 = the current period. */
export type WasteLinePeriodBucket = {
  period_index: number;
  event_count: number;
  /** Image-backed events — the "captures" the Pulse strip ranks on. */
  tagged_capture_count: number;
  total_weight: number;
  total_cost: number;
};

/** A waste "line" (recipe → inventory item → subcategory → category, else
 * unidentified) with a dense per-period array. `line_key` is the canonical
 * key shared with `lib/proof.ts:lineKey`, so it can seed The Proof feed filter. */
export type WasteLineStats = {
  line_key: string;
  label: string;
  recipe_id: string | null;
  inventory_item_id: string | null;
  subcategory_id: string | null;
  category_id: string | null;
  periods: WasteLinePeriodBucket[];
};

export type WasteLineStatsResponse = {
  period_start: string;
  period_length_days: number;
  n_periods: number;
  /** Top-N lines by current-period cost; `remainder` holds everything else. */
  lines: WasteLineStats[];
  remainder: WasteLineStats | null;
};

type Args = {
  /** Required by the endpoint — the dashboard renders one site at a time. */
  siteId: string | null;
  /** Start of the CURRENT (latest) period; earlier periods extend backwards. */
  periodStart: string | null;
  periodLengthDays: number;
  nPeriods: number;
  /** The shared dashboard filters; only the fields the endpoint supports are sent. */
  filters: WasteFilters;
  topN?: number;
  /** Poll interval (ms) while mounted — the Kitchen Display refreshes live. */
  refetchInterval?: number;
};

/**
 * Per-line waste aggregates across N consecutive periods (POS2-867). Feeds the
 * Pulse strip (and later What's-working / Examine). Runs only once a site and a
 * bounded period exist — the all-time range has no "period" to compare.
 */
export function useLineStats({ refetchInterval, ...args }: Args) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = lineStatsParams(args);

  return useQuery<WasteLineStatsResponse>({
    queryKey: ["line-stats", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/line-stats?${params.toString()}`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteLineStatsResponse };
      return body.data;
    },
    enabled: !!args.siteId && !!args.periodStart && !!headers.Authorization.replace("Bearer ", ""),
    refetchInterval,
    placeholderData: (prev) => prev,
  });
}

/** The `/line-stats` query for one call — split out so the params are testable. */
export function lineStatsParams({
  siteId,
  periodStart,
  periodLengthDays,
  nPeriods,
  filters,
  topN,
}: Omit<Args, "refetchInterval">): URLSearchParams {
  const params = new URLSearchParams();
  if (periodStart) params.set("period_start", periodStart);
  params.set("period_length_days", String(periodLengthDays));
  params.set("n_periods", String(nPeriods));
  if (siteId) params.set("site_id", siteId);
  if (topN != null) params.set("top_n", String(topN));
  if (filters.kitchen_id) params.set("kitchen_id", filters.kitchen_id);
  if (filters.device_serial) params.set("device_serial", filters.device_serial);
  if (filters.waste_type) params.set("waste_type", filters.waste_type);
  if (filters.classification_level != null) {
    params.set("classification_level", String(filters.classification_level));
  }
  if (filters.status) params.set("status", filters.status);
  if (filters.service_time) params.set("service_time", filters.service_time);
  if (filters.is_confirmed != null) params.set("is_confirmed", String(filters.is_confirmed));
  // Site-local "HH:MM" time-of-day window, applied within each day (POS2-1483).
  if (filters.time_from) params.set("time_from", filters.time_from);
  if (filters.time_to) params.set("time_to", filters.time_to);
  return params;
}
